import React, { useState, useEffect } from "react";
import PublicHeader from "../components/PublicHeader";
import { FaFish } from "react-icons/fa";
import { GiWheat, GiBirdTwitter, GiHand, GiEarthAmerica } from "react-icons/gi";
import { BsCalendar } from "react-icons/bs";
import { Domaine } from "../types/Domaine";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/OffreDetailsPage.css";
import SiteMenu from "../components/SiteMenu";
import { IconType } from "react-icons";
import { useAuth } from "../context/AuthContext";
import { OffreService } from "../services/OffreService";
import { AO } from "../types/AO";
import { FaDownload } from "react-icons/fa";
import { ENV } from "../constants/env";

const OffreDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { id } = useParams<{ id: string }>(); // Récupère l'id depuis l'URL
  const [offre, setOffre] = useState<AO | null>(null);
  const [loading, setLoading] = useState(true);

  const [domaines] = useState<Domaine[]>([
    { label: "Agriculture", icon: GiWheat, sousDomaines: [{ label: "Structure et Démographie des exploitants" }, { label: "Foncier et Ressources" }, { label: "Contraintes Agricoles" }] },
    { label: "Élevage", icon: GiBirdTwitter, sousDomaines: [{ label: "Effectifs du bétail" }, { label: "Types d’élevage" }, { label: "Production animale" }] },
    { label: "Pêche & Aquaculture", icon: FaFish, sousDomaines: [{ label: "Techniques de pêche" }, { label: "Zones de pêche" }, { label: "Production halieutique" }] },
    { label: "Artisanat", icon: GiHand, sousDomaines: [{ label: "Identification des artisans" }, { label: "Activités artisanales" }, { label: "Production artisanale" }] },
    { label: "Changement Climatique", icon: GiEarthAmerica, sousDomaines: [{ label: "Vulnérabilité climatique" }, { label: "Émissions CO₂" }, { label: "Mesures d’adaptation" }] }
  ]);

  const activities = [
    { label: "Appel d'offre", path: "/offre" },
    { label: "Projet", path: "/project" },
    { label: "Calendrier", path: "/calendar" },
    { label: "Événements", path: "/event" }
  ];

  const SafeIcon: React.FC<{ icon?: IconType; className?: string; size?: number }> = ({ icon, className, size }) => {
    if (!icon) return null;
    const IconComp = icon as React.ComponentType<any>;
    return <IconComp className={className} size={size} />;
  };

  // --- useEffect pour récupérer un AO par son ID ---
  useEffect(() => {
    if (!id) return;
    const fetchAO = async () => {
      try {
        const data = await OffreService.getAOById(api, Number(id));
        setOffre(data);
      } catch (error) {
        console.error("Erreur récupération AO :", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAO();
  }, [api, id]);

  if (loading) return <div>Chargement...</div>;
  if (!offre) return <div>Aucun appel d'offre trouvé.</div>;

  return (
    <div className="offre-details-wrapper">
      <div className="map-page-header mb-3">
        <PublicHeader />
      </div>

      <div className="container-fluid">
        <div className="row">
          {/* Menu latéral via SiteMenu */}
          <div className="col-12 col-md-3 col-lg-2 mb-3 mb-md-0">
            <SiteMenu activities={activities} />
          </div>

          {/* Contenu principal */}
          <main className="col-12 col-md-9 col-lg-10 offre-page-content">
            <h4 className="breadcrumb">
              Les appels d’offres <span className="text-green">/ {offre.titre}</span>
            </h4>

            <h3 className="offer-title">{offre.titre}</h3>

            <div className="offer-status d-flex align-items-center gap-2">
              <SafeIcon icon={BsCalendar} className="calendar-icon text-success" size={20} />
              <span>Clôturé le : <b>{offre.dateCloturation}</b></span>
            </div>

            <p>{offre.description}</p>

            <h5 className="subtitle">Informations supplémentaires</h5>
            <ul className="custom-list">
              {offre.exigence.map((item, index) => {
                const lignes = item.replace(/[\[\]"]/g, '').split(',');
                return lignes.map((line, lineIndex) => (
                  <li key={`${index}-${lineIndex}`}>{line.trim()}</li>
                ));
              })}
            </ul>

            {offre.path && (
              <div className="download-wrapper mt-3">
                {offre.path ? (
                  <a
                    href={`${ENV.API_URL}/ao/files/${offre.path}`}
                    className="btn-download"
                    download
                    title="Télécharger le DAO"
                  >
                    <SafeIcon icon={FaDownload} size={16} />
                  </a>
                ) : (
                  "-"
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default OffreDetailsPage;
